import { createContext, useContext, type PropsWithChildren, type FC, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLocationHistory } from '~/context/index.js'

type ProfileContextValue = {
  getInitialReferrer: () => string | null
}

const ProfileContext = createContext<ProfileContextValue | null>(null)

export function useProfileContext(): ProfileContextValue {
  const context = useContext(ProfileContext)

  if (!context) {
    throw new Error('useProfileContext must be used within a ProfileContextProvider')
  }

  return context
}

export const ProfileContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { getCurrentPath } = useLocationHistory()
  const [params] = useSearchParams()
  const initialReferrerRef = useRef<string | null>(null)
  const hasInitializedRef = useRef(false)

  if (!hasInitializedRef.current) {
    initialReferrerRef.current = params.get('initialReferrer') ?? getCurrentPath()
    hasInitializedRef.current = true
  }

  const value: ProfileContextValue = useMemo(
    () => ({
      getInitialReferrer: () => initialReferrerRef.current,
    }),
    []
  )

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
}
